import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Map } from 'mapbox-gl';
import { useDispatch } from 'react-redux';
import { point, lineString, multiLineString } from '@turf/helpers';
import length from '@turf/length';
import center from '@turf/center';

import { makeEmptyGeojson } from 'modules/geojson/utils';
import {
  useHandleClicksOnMap,
  useHandleMovePoints,
} from 'modules/mapInteracting';
import { InteractionLayersSources, InteractionLayers } from 'modules/mapLayers';
import { MapEvent } from 'modules/map/types';
import { hideAllUi, showAllUi } from 'modules/metadata';
import { Route, RouteWaypoint, WaypointType } from '../types';

type Coordinate = [number, number];

type PointFeature = { type: WaypointType.Point; coordinates: Coordinate };
type GateFeature = { type: WaypointType.Gate; coordinates: Coordinate[] };
type CreationFeature = PointFeature | GateFeature;

type FlatVertex = {
  coordinates: Coordinate;
  isGate: boolean;
  isMidpoint: boolean;
};

const GATE_POINTS = 2;

const gateMidpoint = ([a, b]: Coordinate[]): Coordinate =>
  center(lineString([a, b])).geometry.coordinates as Coordinate;

// Every vertex, flattened in click order. A complete gate also contributes a
// midpoint vertex (isMidpoint) so the route line can pass through the gate's centre.
const flattenCoordinates = (features: CreationFeature[]): FlatVertex[] =>
  features.reduce<FlatVertex[]>((acc, feature) => {
    if (feature.type !== WaypointType.Gate) {
      return [
        ...acc,
        { coordinates: feature.coordinates, isGate: false, isMidpoint: false },
      ];
    }
    const endpoints = feature.coordinates.map((coordinates) => ({
      coordinates,
      isGate: true,
      isMidpoint: false,
    }));
    if (feature.coordinates.length < GATE_POINTS) {
      return [...acc, ...endpoints];
    }
    return [
      ...acc,
      ...endpoints,
      {
        coordinates: gateMidpoint(feature.coordinates),
        isGate: true,
        isMidpoint: true,
      },
    ];
  }, []);

// A gate is only valid once it has both of its points.
const hasIncompleteGate = (features: CreationFeature[]): boolean =>
  features.some(
    (feature) =>
      feature.type === WaypointType.Gate &&
      feature.coordinates.length < GATE_POINTS,
  );

// Replace the coordinate at a flattened vertex index (used while dragging).
const updateCoordinateAt = (
  features: CreationFeature[],
  flatIndex: number,
  coordinate: Coordinate,
): CreationFeature[] => {
  let index = flatIndex;
  return features.map((feature) => {
    if (index < 0) return feature;
    if (feature.type === WaypointType.Point) {
      if (index === 0) {
        index = -1;
        return { ...feature, coordinates: coordinate };
      }
      index -= 1;
      return feature;
    }
    if (index < feature.coordinates.length) {
      const coordinates = [...feature.coordinates];
      coordinates[index] = coordinate;
      index = -1;
      return { ...feature, coordinates };
    }
    index -= feature.coordinates.length;
    return feature;
  });
};

const useCreateRouteOnMap = (
  map: Map,
  distance: MutableRefObject<number>,
  initialRoute?: Route,
  waypointType: WaypointType = WaypointType.Point,
): {
  isNotSinglePoint: boolean;
  handleUndo: () => void;
  getRoute: () => RouteWaypoint[];
  isEmpty: boolean;
} => {
  const dispatch = useDispatch();
  const features = useRef<CreationFeature[]>(
    initialRoute
      ? initialRoute.route.map(({ coordinates }) =>
          Array.isArray(coordinates[0])
            ? {
                type: WaypointType.Gate,
                coordinates: coordinates as Coordinate[],
              }
            : {
                type: WaypointType.Point,
                coordinates: coordinates as Coordinate,
              },
        )
      : [],
  );
  // Read the latest selected type inside the (stable) click handler.
  const waypointTypeRef = useRef(waypointType);
  waypointTypeRef.current = waypointType;

  const movingPointIndex = useRef<number | null>(0);
  const [isNotSinglePoint, setIsNotSinglePoint] = useState(!!initialRoute);
  const [isEmpty, setIsEmpty] = useState(!initialRoute);

  const getRoute = useCallback((): RouteWaypoint[] => {
    return features.current.map(({ coordinates }) => ({ coordinates }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateMap = useCallback((newFeatures: CreationFeature[]) => {
    const vertices = flattenCoordinates(newFeatures);
    // Midpoints are route-line only: not draggable, so they stay out of the
    // indexed circle features that the move handler relies on.
    const draggableVertices = vertices.filter(({ isMidpoint }) => !isMidpoint);
    const pointsFeature = draggableVertices.map(
      ({ coordinates, isGate }, index) => point(coordinates, { index, isGate }),
    );
    const pathCoordinates = vertices
      .filter(({ isGate, isMidpoint }) => !isGate || isMidpoint)
      .map(({ coordinates }) => coordinates);
    const gateFeatures = newFeatures
      .filter(
        (feature) =>
          feature.type === WaypointType.Gate &&
          feature.coordinates.length === GATE_POINTS,
      )
      .map((feature) =>
        lineString((feature as GateFeature).coordinates, {
          isGate: true,
        }),
      );
    const routeLine =
      pathCoordinates.length > 1 ? [multiLineString([pathCoordinates])] : [];

    const geojson = makeEmptyGeojson();
    geojson.features = [...pointsFeature, ...routeLine, ...gateFeatures];
    map
      .getSource(InteractionLayersSources.RouteCreation)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      .setData(geojson);
    distance.current = routeLine.length ? length(routeLine[0]) : 0;
    setIsEmpty(draggableVertices.length === 0);
    setIsNotSinglePoint(
      draggableVertices.length > 1 && !hasIncompleteGate(newFeatures),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUndo = useCallback(() => {
    const newFeatures = [...features.current];
    const last = newFeatures[newFeatures.length - 1];
    if (!last) return;
    if (last.type === WaypointType.Gate && last.coordinates.length > 1) {
      newFeatures[newFeatures.length - 1] = {
        ...last,
        coordinates: last.coordinates.slice(0, -1),
      };
    } else {
      newFeatures.pop();
    }
    features.current = newFeatures;
    updateMap(newFeatures);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMap]);

  const handleClick = useCallback(
    (ev: mapboxgl.MapboxEvent & mapboxgl.EventData) => {
      const coordinate: Coordinate = [ev.lngLat.lng, ev.lngLat.lat];
      const newFeatures = [...features.current];
      if (waypointTypeRef.current === WaypointType.Gate) {
        const last = newFeatures[newFeatures.length - 1];
        if (
          last &&
          last.type === WaypointType.Gate &&
          last.coordinates.length < GATE_POINTS
        ) {
          // Second click completes the open gate's line.
          newFeatures[newFeatures.length - 1] = {
            ...last,
            coordinates: [...last.coordinates, coordinate],
          };
        } else {
          // First click of a new gate.
          newFeatures.push({
            type: WaypointType.Gate,
            coordinates: [coordinate],
          });
        }
      } else {
        newFeatures.push({ type: WaypointType.Point, coordinates: coordinate });
      }
      features.current = newFeatures;
      updateMap(newFeatures);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateMap],
  );

  const handleMove = useCallback(
    (ev: MapEvent) => {
      if (movingPointIndex.current === null) return;
      const newFeatures = updateCoordinateAt(
        features.current,
        movingPointIndex.current,
        [ev.lngLat.lng, ev.lngLat.lat],
      );
      features.current = newFeatures;
      updateMap(newFeatures);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [updateMap],
  );

  const handleMouseEnter = useCallback((ev: MapEvent) => {
    movingPointIndex.current =
      (ev.features && ev.features[0].properties?.index) ?? null;
  }, []);
  useHandleMovePoints(
    map,
    InteractionLayers.RouteCreationPoints,
    handleMove,
    handleMouseEnter,
  );

  const handleCleanupMonitoringClick = useCallback(() => {
    map
      .getSource(InteractionLayersSources.MeasurementTool)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      .setData(makeEmptyGeojson());
  }, [map]);
  useHandleClicksOnMap(map, handleClick, handleCleanupMonitoringClick);

  useEffect(() => {
    dispatch(hideAllUi());
    updateMap(features.current);
    return (): void => {
      dispatch(showAllUi());
      map.getCanvas().style.cursor = '';
      map
        .getSource(InteractionLayersSources.RouteCreation)
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        .setData(makeEmptyGeojson());
    };
  }, [dispatch, map, updateMap]);
  return {
    isNotSinglePoint,
    handleUndo,
    getRoute,
    isEmpty,
  };
};

export default useCreateRouteOnMap;
